import { Download, MouseSimple, Warning } from "@phosphor-icons/react";
import {
  Center,
  Html,
  Loader,
  OrbitControls,
  Stage,
  useGLTF,
} from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { Button } from "@replicate/ui";
import { Leva, useControls } from "leva";
import { Component, Suspense, useRef, type ReactNode } from "react";
import type { Object3D } from "three";

type Lighting =
  | "lobby"
  | "studio"
  | "apartment"
  | "city"
  | "dawn"
  | "forest"
  | "night"
  | "park"
  | "sunset"
  | "warehouse";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children?: ReactNode;
  url?: string;
}
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: undefined };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4 relative bg-r8-gray-1 z-50">
          <div className="p-4 bg-r8-red-3 border border-r8-red-5 rounded-full text-r8-red-10">
            <Warning weight="thin" size={40} />{" "}
          </div>
          <div className="flex flex-col items-center gap-1 font-mono">
            <p>
              {this.state.error?.toString() || "An unknown error has occurred"}
            </p>
            <p className="text-r8-sm text-r8-gray-10">
              You may need to enable webGL in your browser.
            </p>
          </div>
          <Button
            startIcon={<Download />}
            /* biome-ignore lint/a11y/useAnchorContent: handled by ariakit */
            render={<a download href={this.props.url} />}
          >
            Download .glb
          </Button>
        </div>
      );
    }

    return this.props?.children;
  }
}

function isMeshType(object?: Object3D): object is THREE.Mesh {
  return object?.type === "Mesh";
}

function GLBMesh({
  url,
  brightness,
  contrast,
}: { url: string; brightness: number; contrast: number }) {
  const { nodes } = useGLTF(url);
  const groupRef = useRef(null);

  return (
    <group ref={groupRef} dispose={null}>
      {Object.entries(nodes).map(([key, value]) => {
        if (!isMeshType(value)) return null;
        const clonedMaterial = Array.isArray(value.material)
          ? value.material[0].clone()
          : value.material.clone();

        // reduce material brightness
        clonedMaterial.toneMapped = false;
        if ("metalness" in clonedMaterial) {
          (clonedMaterial as THREE.MeshStandardMaterial).metalness = contrast;
        }
        return (
          <mesh
            key={key}
            castShadow
            receiveShadow
            geometry={value.geometry}
            material={clonedMaterial}
          >
            <ambientLight intensity={brightness} />
          </mesh>
        );
      })}
    </group>
  );
}

function StageWrapper({
  url,
  lighting,
  brightness,
  contrast,
}: { url: string; lighting: Lighting; brightness: number; contrast: number }) {
  return (
    <Stage shadows preset="upfront" environment={lighting}>
      <Center>
        <GLBMesh url={url} brightness={brightness} contrast={contrast} />
      </Center>
    </Stage>
  );
}

export default function GLBPreview({ url }: { url: string }) {
  const { lighting, brightness, contrast } = useControls({
    lighting: {
      options: {
        Lobby: "lobby",
        Studio: "studio",
        Apartment: "apartment",
        City: "city",
        Dawn: "dawn",
        Forest: "forest",
        Night: "night",
        Park: "park",
        Sunset: "sunset",
        Warehouse: "warehouse",
      },
    },
    brightness: {
      value: 5,
      min: 0.5,
      max: 10,
      step: 0.1,
    },
    contrast: {
      value: 0.25,
      min: 0,
      max: 0.9,
      step: 0.05,
    },
  } as const);
  return (
    <div className="aspect-[3/4] border border-r8-gray-6 relative group">
      <div className="absolute top-4 right-4 z-[1]">
        <Leva fill titleBar={{ drag: false }} />
      </div>
      <div className="absolute top-4 left-4 opacity-100 group-hover:opacity-0 transition-opacity">
        <div className="flex items-center gap-2 text-r8-gray-11">
          <MouseSimple />
          <p className="text-r8-xs">
            Use your mouse to zoom and rotate the model
          </p>
        </div>
      </div>
      <ErrorBoundary url={url}>
        <Canvas shadows>
          <Suspense
            fallback={
              <Html>
                <Loader
                  innerStyles={{
                    background: "var(--gray-6)",
                  }}
                  barStyles={{
                    background: "var(--gray-12)",
                  }}
                  dataStyles={{
                    color: "var(--gray-12)",
                  }}
                />
              </Html>
            }
          >
            <StageWrapper
              url={url}
              lighting={lighting}
              brightness={brightness}
              contrast={contrast}
            />
            <OrbitControls />
          </Suspense>
        </Canvas>
      </ErrorBoundary>
    </div>
  );
}
